/**
 * ENG-9874 / ENG-10104 — the link between a guardrail TEMPLATE and the code that
 * enforces it, and the declaration of WHAT KIND of link that is.
 *
 * ## Why this file exists
 *
 * A guardrail can be seeded into the catalog, applied to an org, resolved
 * across scopes, rendered into an agent's CLAUDE.md under the heading
 * "Enforced (must comply — violation blocks the action)", and enforce
 * absolutely nothing — and until this file, nothing in the system could notice.
 *
 * `resolveGuardrails` (resolver.ts) decides WHICH guardrail wins between the
 * org, team and agent scopes and at what enforcement level. It does not, and is
 * not meant to, dispatch to an enforcement point. Each guardrail's enforcer is a
 * hand-written module elsewhere, tied to the template only by convention on the
 * shared id string. Convention is not checkable, so nobody checked.
 *
 * MEASURED 2026-09-02 against `packages/supabase/seeds/guardrail-templates.json`:
 * of 18 templates, **4** are referenced anywhere in runtime source and **14**
 * are not. The four inert ones that hurt most are the ones a charter presents as
 * blocking: `require-approval-for-actions`, `data.pii_access` and
 * `data.credential_access` are rendered to agents as "violation blocks the
 * action" while no code reads them.
 *
 * That is not a naming quibble. ENG-8562 is the incident: an agent told a user
 * an email would be held for approval, because its charter said so, and the send
 * went out. The agent was not lying — its charter was.
 *
 * ## ENG-10104: why a boolean was not enough
 *
 * The first cut of this file carried an `enforced | inert` binary, and
 * `isEnforced()` answered the coarser question "is this id referenced anywhere
 * in runtime source". Its four true answers were not four of a kind. Consuming
 * that boolean to decide what renders as Enforced would have fixed the fourteen
 * and left the ENG-7829 bug standing on the entries it passed — the same defect,
 * rebuilt inside its own fix.
 *
 * So the declaration is now ADR-0080 decision 1's four-state mechanism:
 *
 *   - `enforced` + `stage: 'live'`   — a module reads the id and can block.
 *   - `enforced` + `stage: 'shadow'` — the module exists and deliberately does
 *     not act yet. Must say since when (`darkSince`) and what is holding it
 *     (`blockedBy`); those two fields are what stop a dark control being
 *     indistinguishable from a live one.
 *   - `delegated` — read by ANOTHER definition's enforcement point. Records the
 *     real relationship instead of forcing the entry to either name a module
 *     that is not its own (a lie) or claim advisory (discarding true
 *     information). `ownedBy` must resolve to a definition that is itself
 *     `enforced`, and the guard follows the chain rather than checking the field
 *     is populated — otherwise a chain of delegations terminates in nothing and
 *     every link renders as a control.
 *   - `advisory` — nothing reads it. `by-design` is a finished decision and
 *     needs no ticket; `unimplemented` is an omission, and an omission with no
 *     ticket is the thing this whole area is about.
 *   - `retired` — in no catalog. Must be detached from the agents carrying it,
 *     never rendered as a control. This is ENG-9663's ghost, given a state.
 *
 * **The fields are FLAT, and that is load-bearing rather than stylistic.**
 * `scripts/check-guardrail-has-enforcer.mjs` parses this array with a regex that
 * is non-greedy to the first `\s*},`. A nested `mechanism: { … }` would end the
 * match at the inner brace, hiding whatever `enforcer:` or `ticket:` followed
 * it — and the guard's own `id:`-count assertion would still balance, so it
 * would under-parse and report a clean tree. A guard blinded by the shape of the
 * thing it guards is this file's thesis turned on itself.
 *
 * ## What this file is, and what it is NOT
 *
 * It is a declaration of the CURRENT state, not an aspiration. Fourteen entries
 * are `advisory`, and that is the honest reading of main today. The value is not
 * that it fixes them — it does not — but that:
 *
 *   1. a NEW template cannot be added without declaring a mechanism, because
 *      `check-guardrail-has-enforcer.mjs` fails CI on an unregistered id; and
 *   2. an `advisory` entry is a visible, greppable admission with a ticket
 *      attached, rather than an absence nobody can see; and
 *   3. since ENG-10104, the declaration is CONSUMED — `guardrailCanBlock()`
 *      decides what may render as Enforced, in the agent's prompt and in the
 *      console, so the two surfaces cannot disagree with each other or with
 *      this file.
 *
 * ## Why the enforcer is a MODULE PATH and not a boolean
 *
 * The obvious cheap version is `enforced: true`, or a guard that greps the repo
 * for the id. Both reproduce the bug they are meant to catch.
 *
 * This ticket's own worked example: `agents.risk_tier` is inert, but
 * `tool.risk_tier` genuinely gates (xero-broker returns hard_deny /
 * auto_approve / route_to_approver from it). A repo-wide grep for `risk_tier`
 * finds the WORKING enforcement code and returns a confident false yes. Naming
 * the exact module, and checking that module references the id, is what makes
 * the answer mean something.
 */

import { CALENDAR_CONFIDENTIALITY_DEFINITION_ID, parseCalendarGuardStage } from './calendar-policy.js';

/** ADR-0080: whether the enforcement point acts, or only observes. */
export type GuardrailEnforcementStage = 'live' | 'shadow';

/** A module reads this guardrail and can act on it today. */
export interface LiveEnforcedGuardrail {
  id: string;
  mechanism: 'enforced';
  stage: 'live';
  /**
   * Repo-relative path of the module that reads this guardrail and changes
   * behaviour on it. The guard asserts the file exists AND mentions the id —
   * naming a module that never references it is the failure mode a boolean
   * cannot catch.
   */
  enforcer: string;
  /** One line on what enforcement actually does, for whoever audits next. */
  effect: string;
}

/**
 * The enforcement point exists and deliberately does not act. `darkSince` and
 * `blockedBy` are REQUIRED by the type, not by a CI check: a check that demands
 * a ticket can be satisfied once and regressed by a later edit; a union member
 * that cannot be constructed without one cannot.
 */
export interface ShadowEnforcedGuardrail {
  id: string;
  mechanism: 'enforced';
  stage: 'shadow';
  enforcer: string;
  effect: string;
  /** ISO date the enforcement point went dark, or shipped dark. */
  darkSince: string;
  /** The Linear issue holding it dark. */
  blockedBy: string;
}

export type EnforcedGuardrail = LiveEnforcedGuardrail | ShadowEnforcedGuardrail;

/**
 * Read by ANOTHER definition's enforcement point. `enforcer` is retained
 * alongside `ownedBy` because the named module genuinely does mention this id —
 * dropping it would weaken the guard's mention-check for no gain.
 */
export interface DelegatedGuardrail {
  id: string;
  mechanism: 'delegated';
  /** Must resolve to an entry that is itself `enforced`. Checked transitively. */
  ownedBy: string;
  enforcer: string;
  effect: string;
}

/** A finished decision that this guardrail is guidance. No ticket owed. */
export interface AdvisoryByDesignGuardrail {
  id: string;
  mechanism: 'advisory';
  reason: 'by-design';
  note?: string;
}

/** Nobody built the enforcement point. The ticket is the whole point of the entry. */
export interface AdvisoryUnimplementedGuardrail {
  id: string;
  mechanism: 'advisory';
  reason: 'unimplemented';
  /** The ticket tracking it. Required — an advisory entry without one is just a shrug. */
  ticket: string;
  note?: string;
}

export type AdvisoryGuardrail = AdvisoryByDesignGuardrail | AdvisoryUnimplementedGuardrail;

/** Removed from the catalog. Applied rows must be detached, never rendered as a control. */
export interface RetiredGuardrail {
  id: string;
  mechanism: 'retired';
  ticket: string;
  supersededBy?: string;
  note?: string;
}

/** Everything that is not an enforcement point of its own or a delegation to one. */
export type InertGuardrail = AdvisoryGuardrail | RetiredGuardrail;

export type GuardrailEnforcerEntry = EnforcedGuardrail | DelegatedGuardrail | InertGuardrail;

/**
 * ENG-10104 narrowed this. It used to mean "has an `enforcer` field", which was
 * also true of the two delegated email entries — so it answered "is this id
 * referenced in runtime source", not "does this guardrail have its own
 * enforcement point". Callers wanting the question that governs rendering want
 * `guardrailCanBlock()`, not this.
 */
export function isEnforced(e: GuardrailEnforcerEntry): e is EnforcedGuardrail {
  return e.mechanism === 'enforced';
}

export function isDelegated(e: GuardrailEnforcerEntry): e is DelegatedGuardrail {
  return e.mechanism === 'delegated';
}

/**
 * Every id in `packages/supabase/seeds/guardrail-templates.json`, exactly once.
 * Enforced by `scripts/check-guardrail-has-enforcer.mjs` in BOTH directions:
 * a seeded template missing here fails, and an entry here for an id no longer
 * seeded fails too — the second is the ENG-9663 shape, where a definition was
 * deleted from the library while applied rows kept rendering "Enforced".
 */
export const GUARDRAIL_ENFORCERS: readonly GuardrailEnforcerEntry[] = [
  // ---- Enforced: this definition has its own enforcement point -------------
  {
    id: 'email.domain_restrict',
    mechanism: 'enforced',
    stage: 'live',
    enforcer: 'packages/api/src/lib/email-domain-guard.ts',
    effect:
      'enforceEmailDomainGuard runs at the top of both agent-egress paths; internal_only with zero verified domains takes an explicit AUDITED no-op branch rather than failing open silently.',
  },
  {
    // MEASURED, and it corrects a claim ADR-0080 makes about this definition.
    //
    // The ADR reports calendar as `enforced` + `stage: 'shadow'`, blocked by
    // ENG-5868 (caller identity is not threaded to `callIntegrationTool`). Read
    // literally that says an org cannot get redaction today, and that is not
    // what the code does. `decideCalendarRedactionMode` (calendar-policy.ts) is
    // FAIL-CLOSED: with the caller unresolvable it returns `redact`, not
    // pass-through. `redactCalendarToolResponseIfNeeded` then calls
    // `applyRedactionToResponse` for every response whenever `config.stage` is
    // `enforce`. So an org that sets the stage gets real redaction — ENG-5868
    // costs the control its PRECISION (it cannot spare the principal), not its
    // ability to act.
    //
    // The stage is therefore per-ORG configuration, which ADR-0080 decision 1
    // is explicit is the effectiveness axis the static union deliberately does
    // not carry. `effectiveCalendarEnforcement` in the CLAUDE.md renderer owns
    // it, and since ENG-10104 caps `enforce` to `warn` whenever the org's stage
    // is not `enforce`.
    id: 'calendar.confidentiality',
    mechanism: 'enforced',
    stage: 'live',
    enforcer: 'packages/api/src/lib/calendar-confidentiality-guard.ts',
    effect:
      'at config.stage=enforce, redacts summary/description/attendees/location out of calendar read responses; at shadow/warn it writes a would_redact audit row and returns the response unchanged.',
  },

  // ---- Delegated: read by another definition's enforcement point -----------
  //
  // Both can block, because their owner's point is live — which is the question
  // `guardrailCanBlock` asks and the right answer to it. It is NOT the whole
  // story for these two, and the gap has its own ticket: the org's email guard
  // STAGE is threaded into the prompt shape for `email.domain_restrict`'s id
  // alone (`host-runtime.ts`, the ENG-7829 fix), so a row of either id stored at
  // `enforce` on a shadow-stage org still renders under "must comply" with
  // nothing capping it. That is the effectiveness axis, not the mechanism one —
  // ENG-10110.
  {
    id: 'email.require_approval',
    mechanism: 'delegated',
    ownedBy: 'email.domain_restrict',
    enforcer: 'packages/api/src/lib/email-domain-guard.ts',
    effect: 'routes a send to an approver instead of letting it leave.',
  },
  {
    id: 'email.mail_rules',
    mechanism: 'delegated',
    ownedBy: 'email.domain_restrict',
    enforcer: 'packages/api/src/lib/email-domain-guard.ts',
    effect: 'gates mail-rule creation/modification on the same egress path.',
  },

  // ---- Advisory: seeded, applied, rendered — and read by nothing -----------
  //
  // These are NOT dead config. They are presented to operators in the console
  // and to agents in CLAUDE.md, three of them under a heading that promises the
  // violation is blocked. Listed individually rather than as a wildcard so that
  // fixing one is a one-line diff from `advisory` to `enforced`.
  //
  // The ticket is ENG-9800, not ENG-9874. ENG-9874 catalogued these and is
  // closed; a closed ticket tracks nothing, which is the same shrug an absent
  // one would be. ENG-9800 is the open sweep that owns making them real.
  {
    id: 'require-approval-for-actions',
    mechanism: 'advisory',
    reason: 'unimplemented',
    ticket: 'ENG-9800',
    note: 'Rendered to agents as "violation blocks the action". Nothing reads it. The ENG-8562 incident is this guardrail\'s promise, unkept.',
  },
  {
    id: 'data.pii_access',
    mechanism: 'advisory',
    reason: 'unimplemented',
    ticket: 'ENG-9800',
    note: 'Rendered as Enforced with block_export semantics; no export path consults it.',
  },
  {
    id: 'data.credential_access',
    mechanism: 'advisory',
    reason: 'unimplemented',
    ticket: 'ENG-9800',
    note: 'Rendered as Enforced with block_read/block_write; no credential path consults it.',
  },
  { id: 'comm.social_posting', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'compliance.audit_all', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'no-external-network', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'no-pii-in-output', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'restrict-file-access', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'schedule.operating_hours', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'spend.hourly_limit', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'tool.allow_list', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'tool.block_list', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'tool.financial_txn', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
  { id: 'working-hours-only', mechanism: 'advisory', reason: 'unimplemented', ticket: 'ENG-9800' },
];

const BY_ID: ReadonlyMap<string, GuardrailEnforcerEntry> = new Map(
  GUARDRAIL_ENFORCERS.map((e) => [e.id, e]),
);

/** The declared mechanism for a definition id, or undefined if it is unregistered. */
export function guardrailEnforcerEntry(definitionId: string): GuardrailEnforcerEntry | undefined {
  return BY_ID.get(definitionId);
}

/**
 * Can this definition cause the platform to STOP something?
 *
 * The question every rendering surface actually needs, and the reason this is
 * not `isEnforced()`. Answers:
 *
 *   - `enforced` + live         → yes.
 *   - `enforced` + shadow       → NO. Shadow means the point observes and
 *                                 returns unchanged; that is calendar's posture
 *                                 for three months while the console said
 *                                 Enforced (ENG-7829's family).
 *   - `delegated`               → whatever its owner answers, followed
 *                                 transitively, with cycle protection. A chain
 *                                 that does not terminate in a live enforced
 *                                 entry is `false`, so a broken delegation
 *                                 degrades to advisory rather than inheriting a
 *                                 claim nobody makes.
 *   - `advisory` / `retired`    → no.
 *   - UNREGISTERED              → no, and this is deliberate rather than a
 *                                 fallback. The guard requires every SEEDED
 *                                 template to appear here, so an id that is
 *                                 absent is one of: a user-authored definition
 *                                 (`source: 'user'`, which has no enforcement
 *                                 point by construction), or a ghost still
 *                                 applied after being removed from the catalog
 *                                 — ENG-9663 was exactly that, and it rendered
 *                                 as Enforced for eight weeks. Preserving
 *                                 enforcement for an unknown id would leave
 *                                 every one of those a phantom, which is the
 *                                 hole this function exists to close.
 */
export function guardrailCanBlock(definitionId: string): boolean {
  return resolveCanBlock(definitionId, (id) => BY_ID.get(id));
}

/**
 * The chain walk, with the lookup injected.
 *
 * Exported for one reason, and it is a real one rather than test convenience:
 * the cycle branch is **unreachable from the live registry**, because
 * `check-guardrail-has-enforcer.mjs` rejects any chain that does not terminate
 * in an `enforced` entry. A test written against `guardrailCanBlock` therefore
 * cannot exercise it — and mutating `GUARDRAIL_ENFORCERS` would not help, since
 * `BY_ID` is built once at module load and would not see the change. So the
 * branch could only be asserted by injecting a lookup. (CodeRabbit, PR #5669,
 * which spotted that the test claiming to cover the cycle asserted a chain that
 * resolves cleanly.)
 *
 * The branch stays in even though the guard makes it unreachable today: a
 * renderer that hangs is worse than one that renders the wrong header, and the
 * guard is a separate program that can be edited.
 */
export function resolveCanBlock(
  definitionId: string,
  lookup: (id: string) => GuardrailEnforcerEntry | undefined,
): boolean {
  const seen = new Set<string>();
  let id = definitionId;
  for (;;) {
    if (seen.has(id)) return false; // delegation cycle
    seen.add(id);
    const entry = lookup(id);
    if (!entry) return false;
    if (entry.mechanism === 'enforced') return entry.stage === 'live';
    if (entry.mechanism !== 'delegated') return false;
    id = entry.ownedBy;
  }
}

/**
 * ENG-10104 — what a rendering surface should actually SAY about one row.
 *
 * `guardrailCanBlock` answers the static, fleet-wide question. It is necessary
 * and, on its own, not sufficient: ADR-0080 decision 1 names a second axis it
 * deliberately cannot carry — *does this enforce for this org, right now?* — and
 * warns that declaring only the first "relabels fourteen phantoms and leaves a
 * fifteenth telling exactly the same lie".
 *
 * This resolves both, for the definitions whose second axis is READABLE from
 * the row itself — calendar's from its `config.stage`, and (ENG-10457) the
 * email domain guard's from the `inert` annotation GET /guardrails computes:
 *
 *   - `blocking`   — a live point, and nothing in this row disarms it.
 *   - `monitoring` — a live point that this scope has not armed. Calendar at
 *                    `stage: 'shadow'` (the seed default) evaluates the
 *                    redaction, writes the audit row, and returns the response
 *                    unchanged; an `internal_only` email guard with no verified
 *                    domain no-ops and allows every recipient. ADR-0080 names
 *                    this state *Monitoring* and says explicitly it "must not
 *                    render as Enforced".
 *   - `advisory`   — nothing reads this definition at all.
 *
 * **The bound, stated rather than left implicit.** `email.require_approval` and
 * `email.mail_rules` also have a second axis, and it is NOT in the row — the
 * stage lives on `organizations.email_guard_stage`, so nothing holding only a
 * guardrail row can resolve it, in the console or anywhere else. Those two
 * therefore answer `blocking` here even on a shadow-stage org. That is ENG-10110,
 * and the honest reading of this function is "resolves effectiveness where it is
 * knowable from the row", not "resolves effectiveness".
 *
 * ENG-10457 moved one definition ACROSS that bound rather than removing it: the
 * email domain guard's inertness is org data, not row data, so the API resolves
 * it server-side and hands the answer down on the row. That is the shape any
 * future case has to take — this function cannot query, so a caller that knows
 * must tell it. What it must never do is guess, which is why `inert` absent
 * means "not known to be inert" and not "armed".
 */
export type GuardrailEffectiveMode = 'blocking' | 'monitoring' | 'advisory';

export function guardrailEffectiveMode(
  definitionId: string | undefined,
  config?: Record<string, unknown> | null,
  row?: { inert?: boolean } | null,
): GuardrailEffectiveMode {
  // An absent id cannot be looked up, and the honest answer for one is the same
  // as for an unregistered one — see `guardrailCanBlock` on why unknown means
  // advisory rather than "trust the stored value".
  if (!definitionId) return 'advisory';
  if (!guardrailCanBlock(definitionId)) return 'advisory';
  if (definitionId === CALENDAR_CONFIDENTIALITY_DEFINITION_ID) {
    return parseCalendarGuardStage(config?.['stage']) === 'enforce' ? 'blocking' : 'monitoring';
  }
  // ENG-10457 — the second axis, where the ROW carries it.
  //
  // This is the case the docstring above was written around and could not
  // answer: `email.domain_restrict` at `internal_only` with no verified domain
  // is a live point, a live stage, correct code, and ZERO effect, because an
  // empty allow set cannot be enforced. ENG-9841 computed exactly that and put
  // it on the row; until now nothing read it, so this function returned
  // `blocking` and the console chipped a guard that allows every external
  // recipient as though it were stopping them.
  //
  // Deliberately NOT a fourth mode. `monitoring` already means "a real
  // enforcement point that this scope has not armed", which is precisely this
  // state — inventing a third word for it would split one idea across two
  // vocabularies and leave a reader to work out the difference.
  //
  // Placed AFTER the checks above, never before: an `advisory` row must stay
  // advisory (nothing reads it, so "not armed" would over-claim), and the
  // calendar branch owns its own axis. `inert` can only ever soften a
  // `blocking` answer, never harden a weaker one.
  if (row?.inert === true) return 'monitoring';
  return 'blocking';
}
